import api from './api';
import type {
  BillingInvoice,
  FamilyPlanConfig,
  PaymentGateway,
  PaymentIssue,
  PaymentMethodOnFile,
  PlayerPlanId,
  SubscriptionCategory,
  SubscriptionIntent,
  UserSubscription,
} from '@types/index';
import type { PlayerFamilyCatalog } from '@/content/subscriptionCatalog';
import { normalizePlayerFamilyCatalog, PLAYER_FAMILY_CATALOG } from '@/content/subscriptionCatalog';
import { openSubscriptionCheckout, type SubscriptionCheckout } from '@/utils/subscriptionCheckout';

export async function fetchMySubscription(): Promise<UserSubscription | null> {
  try {
    const res = await api.get('/subscriptions/me');
    if (res.data?.success && res.data.data) {
      return res.data.data as UserSubscription;
    }
  } catch {
    /* unauthenticated or API down */
  }
  return null;
}

export async function confirmStripeCheckout(sessionId: string): Promise<UserSubscription> {
  const res = await api.post('/subscriptions/confirm-stripe', { sessionId });
  if (!res.data?.success || !res.data.data) {
    throw new Error(res.data?.message || 'Could not confirm Stripe payment');
  }
  return res.data.data as UserSubscription;
}

export async function confirmRazorpayCheckout(payload: {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}): Promise<UserSubscription> {
  const res = await api.post('/subscriptions/confirm-razorpay', payload);
  if (!res.data?.success || !res.data.data) {
    throw new Error(res.data?.message || 'Could not confirm Razorpay payment');
  }
  return res.data.data as UserSubscription;
}

export async function purchaseSubscription(
  offerId: string,
  familyConfig?: FamilyPlanConfig,
  intent: SubscriptionIntent = 'purchase',
  extras?: { startDate?: string; provider?: PaymentGateway },
): Promise<UserSubscription> {
  const res = await api.post('/subscriptions/purchase', {
    offerId,
    familyConfig,
    intent,
    startDate: extras?.startDate,
    provider: extras?.provider,
  });
  if (!res.data?.success || !res.data.data) {
    throw new Error(res.data?.message || 'Purchase failed');
  }
  const subscription = res.data.data as UserSubscription;
  const checkout = res.data.checkout as SubscriptionCheckout | undefined;
  if (intent === 'purchase') {
    if (!checkout) {
      throw new Error('Payment provider is not connected yet.');
    }
    const paid = await openSubscriptionCheckout(checkout, confirmRazorpayCheckout);
    if (paid === 'redirect') {
      return subscription;
    }
    return paid;
  }
  return subscription;
}

export async function changeSubscriptionPlan(input: {
  category: SubscriptionCategory;
  planId: PlayerPlanId;
  familyConfig?: Partial<FamilyPlanConfig>;
  provider?: PaymentGateway;
}): Promise<UserSubscription> {
  const res = await api.post('/subscriptions/change-plan', input);
  if (!res.data?.success || !res.data.data) {
    throw new Error(res.data?.message || 'Could not change plan');
  }
  return res.data.data as UserSubscription;
}

export async function prebookSubscription(input: {
  category?: SubscriptionCategory;
  planId?: PlayerPlanId;
  familyConfig?: Partial<FamilyPlanConfig>;
  startDate: string;
  provider?: PaymentGateway;
}): Promise<UserSubscription> {
  const res = await api.post('/subscriptions/prebook', input);
  if (!res.data?.success || !res.data.data) {
    throw new Error(res.data?.message || 'Could not pre-book');
  }
  return res.data.data as UserSubscription;
}

export async function cancelSubscription(): Promise<UserSubscription> {
  const res = await api.post('/subscriptions/cancel');
  if (!res.data?.success || !res.data.data) {
    throw new Error(res.data?.message || 'Could not cancel subscription');
  }
  return res.data.data as UserSubscription;
}

export async function fetchMyInvoices(): Promise<BillingInvoice[]> {
  try {
    const res = await api.get('/subscriptions/invoices');
    if (res.data?.success && Array.isArray(res.data.data)) {
      return res.data.data as BillingInvoice[];
    }
  } catch {
    /* unauthenticated or API down */
  }
  return [];
}

export async function fetchPaymentGateways(): Promise<{ stripe: boolean; razorpay: boolean }> {
  try {
    const res = await api.get('/subscriptions/payment-gateways');
    if (res.data?.success && res.data.data) {
      return {
        stripe: Boolean(res.data.data.stripe),
        razorpay: Boolean(res.data.data.razorpay),
      };
    }
  } catch {
    /* fall through */
  }
  return { stripe: false, razorpay: false };
}

export async function fetchPaymentMethods(): Promise<PaymentMethodOnFile[]> {
  try {
    const res = await api.get('/subscriptions/payment-methods');
    if (res.data?.success && Array.isArray(res.data.data)) {
      return res.data.data as PaymentMethodOnFile[];
    }
  } catch {
    /* fall through */
  }
  return [];
}

export async function savePaymentMethod(input: {
  gateway: PaymentGateway;
  brand?: string;
  last4?: string;
  cardNumber?: string;
  expMonth: number;
  expYear: number;
  holderName?: string;
}): Promise<PaymentMethodOnFile> {
  const res = await api.post('/subscriptions/payment-methods', input);
  if (!res.data?.success || !res.data.data) {
    throw new Error(res.data?.message || 'Could not save card');
  }
  return res.data.data as PaymentMethodOnFile;
}

export async function fetchPaymentHistory(gateway: PaymentGateway): Promise<BillingInvoice[]> {
  try {
    const res = await api.get('/subscriptions/payment-history', { params: { gateway } });
    if (res.data?.success && Array.isArray(res.data.data)) {
      return res.data.data as BillingInvoice[];
    }
  } catch {
    /* fall through */
  }
  return [];
}

export async function submitPaymentIssue(input: {
  issueType: string;
  description: string;
  gateway?: PaymentGateway | string;
}): Promise<PaymentIssue> {
  const res = await api.post('/subscriptions/payment-issues', input);
  if (!res.data?.success || !res.data.data) {
    throw new Error(res.data?.message || 'Could not submit issue');
  }
  return res.data.data as PaymentIssue;
}

export async function fetchPlayerFamilyCatalog(countryHint?: string | null): Promise<PlayerFamilyCatalog> {
  try {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    const params = new URLSearchParams();
    if (countryHint) params.set('country', countryHint);
    const query = params.toString();
    const res = await fetch(`/api/subscription-catalog${query ? `?${query}` : ''}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const data = await res.json();
    if (res.ok && data?.success && data.data?.family) {
      return normalizePlayerFamilyCatalog(data.data.family);
    }
  } catch {
    /* fall through */
  }
  return PLAYER_FAMILY_CATALOG;
}
